import { type Brand } from '@/consts/brands';
import {
  type RegisterPaymentMethodData,
  type RegisterPaymentMethodResponseData,
} from '@/types/payments/types';
import { type HttpError, post } from '@/utils/http/client';
import type { ErrorBag } from '@/utils/http/types';
import { urls } from '@/utils/urls';

const SESSION_EXPIRED_STATUSES = [401, 403, 419];

const storePaymentMethod = async ({
  brand,
  data,
  onSuccess,
  onError,
}: {
  brand: Brand;
  data: RegisterPaymentMethodData;
  onSuccess: (data?: RegisterPaymentMethodResponseData) => void;
  onError: (message: string) => void;
}) => {
  try {
    const response: RegisterPaymentMethodResponseData = await post(
      urls[brand].api.payments.storePaymentMethod,
      data,
    );
    onSuccess(response);
  } catch (exception) {
    const error = exception as HttpError<ErrorBag>;

    /**
     * Handle network errors (no response received)
     */
    if (!error.response) {
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        onError('Request timeout. Please check your connection and try again.');
      } else {
        onError(
          'Network connection lost. Please check your connection and try again.',
        );
      }
      return;
    }

    if (SESSION_EXPIRED_STATUSES.includes(error.response.status)) {
      throw exception;
    }

    if (error.response.status === 503) {
      onError(
        error.response?.data?.message ??
          'Payment processor temporarily unavailable. Please try again.',
      );
      return;
    }

    onError(
      error.response?.data?.message ??
        'An error occurred while updating the payment method.',
    );
  }
};

export const PaymentMethodService = {
  storePaymentMethod,
};
